// @flow

import get from 'lodash/get';
import sanitizeHtml from 'sanitize-html';
import React from 'react';
import type { Node } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Heading, Paragraph, Link, TabAccordion, TabPane, Markdown } from '@riseart/common';
import { Row, Col } from '@riseart/antd-provider';
import { ArtWallPreview, ArtDetails as ArtSpecsDetails, ArtDetailsListItem } from '@riseart/art';
import { nl2br } from '@riseart/fe-utils';
import { renderWallPreviewPicture } from 'shared_services/riseart/utils/artDataUtils';
import { ShipsFrom } from 'shared_components/arts/fragments/ShipsFrom';
import { ArtSpecs } from 'shared_components/arts/specs/Specs';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { CmsModuleFaqs } from 'shared_components/cms/modules/common/Faqs';
import {
  gtmArtTabDetailToggle,
  gtmArtTabFaqToggle,
} from 'shared_data/providers/google/tagmanager/Actions';

import { artTabsHeadingCls, artFaqsAccordionCls } from './Tabs.less';

type Props = {
  intl: Object,
  is3D: boolean,
  artData: Object,
  hasEditorialData: boolean,
  showLeftEditorialColumn: boolean,
  editorials: Object,
  currentLocale: Object,
  activeTab: number,
  cmsTabs: Array<{
    name: string,
    columns: Array<{
      sections: Array<{
        title: string,
        content: string,
      }>,
    }>,
  }>,
  cmsFaqs: Object,
};

/**
 * CmsModuleProductTabs
 */
export const CmsModuleProductTabs = ({
  intl,
  is3D,
  artData,
  hasEditorialData,
  showLeftEditorialColumn,
  editorials,
  currentLocale,
  activeTab,
  cmsTabs,
  cmsFaqs,
}: Props): Node => {
  const dispatch = useDispatch();

  const handleTabChange = (actionCreator) => (tabIdx, openedTabs, tabProps) => {
    dispatch(actionCreator({ tabIdx, openedTabs, tabId: tabProps.tabId }));
  };

  const hasRelatedArticles = editorials.relatedArticles && editorials.relatedArticles.length;
  const showDescription = artData && artData.showDescription && artData.description;
  const calcActiveTab = (activeTab && ((Array.isArray(activeTab) && activeTab) || [activeTab])) || [
    // always open the first tab by default
    0,
    // Open the first two tabs if showDescription is true
    ...(showDescription ? [1] : []),
  ];

  return (
    <TabAccordion active={calcActiveTab} onTabChange={handleTabChange(gtmArtTabDetailToggle)}>
      {[
        ...(showDescription
          ? [
              <TabPane
                header={intl.formatMessage({ id: 'components.art.description' })}
                tabId="artDescription"
                key="artDescription"
              >
                <Markdown skipHtml text={artData.description} />
              </TabPane>,
            ]
          : []),
        <TabPane
          header={intl.formatMessage({
            id: 'components.art.product_info',
          })}
          tabId="art_detail"
          key="artDetail"
        >
          <Row type="flex" justify="space-between" gutter={20}>
            <Col span={24} lg={is3D ? 24 : 10}>
              <ArtSpecs
                data={{
                  ...artData,
                  shipping: artData.isProduct && artData.productCanBuy && artData.shipsFromCode && (
                    <ShipsFrom countryCode={artData.shipsFromCode} />
                  ),
                }}
              />
            </Col>
            {!is3D && (
              <Col span={24} lg={14}>
                <ArtWallPreview
                  label={artData.artworkDimensionsLabel}
                  imageWidth={artData.width}
                  imageHeight={artData.height}
                >
                  {renderWallPreviewPicture({
                    images: artData.images,
                    width: artData.width,
                    height: artData.height,
                    alt: intl.formatMessage(
                      {
                        id: 'components.art.artwork_by_artist',
                      },
                      {
                        title: artData.title,
                        artist: artData.artistName,
                      },
                    ),
                  })}
                </ArtWallPreview>
              </Col>
            )}
          </Row>
        </TabPane>,
        ...(hasEditorialData
          ? [
              <TabPane
                header={intl.formatMessage({
                  id: 'components.art.need_to_know',
                })}
                tabId="need_to_know"
                key="needToKnow"
              >
                <Row type="flex" justify="space-between">
                  {showLeftEditorialColumn ? (
                    <Col span={24} lg={hasRelatedArticles ? 10 : 24}>
                      {editorials.artist && editorials.artist.length > 0 && (
                        <ArtSpecsDetails nodeType="section">
                          <Heading tag="h4" level="5" className={artTabsHeadingCls}>
                            {intl.formatMessage({
                              id: 'components.art.need_to_know',
                            })}
                          </Heading>
                          <div>
                            {editorials.artist.map(({ id, text }) => (
                              <Paragraph key={id}>{text}</Paragraph>
                            ))}
                          </div>
                        </ArtSpecsDetails>
                      )}
                      {editorials.additionalInfo && editorials.additionalInfo.length > 0 && (
                        <ArtSpecsDetails nodeType="section">
                          <Heading tag="h4" level="5" className={artTabsHeadingCls}>
                            {intl.formatMessage({
                              id: 'components.art.additional_info',
                            })}
                          </Heading>
                          {editorials.additionalInfo.map(({ id, value }) => (
                            <Paragraph
                              key={id}
                              innerHtml={nl2br(
                                sanitizeHtml(value.text, {
                                  allowedTags: [],
                                  allowedAttributes: {},
                                }),
                              )}
                            />
                          ))}
                        </ArtSpecsDetails>
                      )}
                    </Col>
                  ) : null}
                  {hasRelatedArticles ? (
                    <Col span={24} lg={showLeftEditorialColumn ? 10 : 24}>
                      <ArtSpecsDetails nodeType="section">
                        <Heading tag="span" level="5" className={artTabsHeadingCls}>
                          {intl.formatMessage({ id: 'components.art.related_content' })}
                        </Heading>
                        <ArtSpecsDetails nodeType="ul">
                          {editorials.relatedArticles.map((item) => {
                            const authorUrl =
                              item.author &&
                              UrlAssembler.authorDetail(
                                item.author.type,
                                item.author.typeId,
                                item.author.typeAlias,
                                { locale: currentLocale },
                              );
                            const author = authorUrl ? (
                              <Link to={authorUrl} title={item.author.fullName}>
                                {item.author.fullName}
                              </Link>
                            ) : (
                              (item.author && item.author.fullName) || null
                            );
                            return (
                              <ArtDetailsListItem key={item.id}>
                                <Heading tag="h5" level="5">
                                  <Link
                                    to={
                                      UrlAssembler.articleDetail(item.id, item.slug, item.type, {
                                        locale: currentLocale,
                                      }) || get(item, 'value.url')
                                    }
                                    title={item.title || get(item, 'value.text')}
                                  >
                                    {item.title || get(item, 'value.text')}
                                  </Link>
                                </Heading>
                                {item.author && (
                                  <FormattedMessage id="common.byName" values={{ name: author }} />
                                )}
                              </ArtDetailsListItem>
                            );
                          })}
                        </ArtSpecsDetails>
                      </ArtSpecsDetails>
                    </Col>
                  ) : null}
                </Row>
              </TabPane>,
            ]
          : []),
        ...(cmsTabs
          ? cmsTabs.map(({ name, columns }, idx) => {
              const columnsLength = columns && columns.length;

              return (
                <TabPane
                  header={name}
                  tabId={typeof name === 'string' ? name.toLowerCase().replace(/[ ]/gi, '_') : idx}
                  key={name || idx}
                >
                  <Row type="flex" justify="space-between">
                    {columns.map(({ sections }, sectionIdx) => (
                      <Col span={24} lg={columnsLength > 1 ? 10 : 24} key={sectionIdx}>
                        {sections.map(({ title, content }) => (
                          <ArtSpecsDetails nodeType="section" key={title}>
                            {title ? (
                              <Heading tag="h4" level="5" className={artTabsHeadingCls}>
                                {title}
                              </Heading>
                            ) : null}
                            {/* eslint-disable-next-line */}
                            <div dangerouslySetInnerHTML={{ __html: content }} />
                          </ArtSpecsDetails>
                        ))}
                      </Col>
                    ))}
                  </Row>
                </TabPane>
              );
            })
          : []),
        ...(cmsFaqs
          ? [
              <TabPane header={cmsFaqs.title} tabId="faqs" key={cmsFaqs.title}>
                <CmsModuleFaqs
                  onTabChange={handleTabChange(gtmArtTabFaqToggle)}
                  questions={cmsFaqs.questions}
                  className={artFaqsAccordionCls}
                />
              </TabPane>,
            ]
          : []),
      ]}
    </TabAccordion>
  );
};
